import { create } from 'zustand';
import {
  ROUTES,
  UserRole,
  type LoginInput,
  type LoginResponse,
  type SessionUser,
} from '@forever/shared';
import { api, clearTokens, getTokens, setTokens } from './api';

type AuthState = {
  user: SessionUser | null;
  initializing: boolean;
  signIn: (input: LoginInput) => Promise<void>;
  signOut: () => Promise<void>;
  restore: () => Promise<void>;
};

export const useAuth = create<AuthState>((set) => ({
  user: null,
  initializing: true,

  async restore() {
    if (!getTokens()) {
      set({ user: null, initializing: false });
      return;
    }
    try {
      const user = await api.get<SessionUser>(ROUTES.auth.me);
      set({ user });
    } catch {
      clearTokens();
      set({ user: null });
    } finally {
      set({ initializing: false });
    }
  },

  async signIn(input) {
    const res = await api.post<LoginResponse>(ROUTES.auth.login, input, { anonymous: true });
    // Panel yalnızca ekip içindir; danışan hesabı buradan giriş yapamaz.
    if (res.user.role === UserRole.CLIENT) {
      throw new Error('Bu panel yalnızca ekip üyeleri içindir. Mobil uygulamayı kullanın.');
    }
    setTokens(res.tokens);
    set({ user: res.user });
  },

  async signOut() {
    const tokens = getTokens();
    try {
      await api.post(ROUTES.auth.logout, { refreshToken: tokens?.refreshToken });
    } catch {
      // Sunucuya ulaşılamasa da yerel oturum kapanır.
    }
    clearTokens();
    set({ user: null });
  },
}));

/** Rol bazlı görünürlük — yalnızca arayüz içindir, asıl kontrol sunucudadır. */
export function useIsAdmin(): boolean {
  return useAuth((s) => s.user?.role === UserRole.ADMIN);
}

export function useCurrentUser(): SessionUser | null {
  return useAuth((s) => s.user);
}
